/**
 * Buscador de registros de otro modelo. Lo comparten el many2one y el many2many.
 *
 * Patron combobox del APG: un `<input role="combobox">` con una `listbox`
 * asociada, `aria-activedescendant` para mover la seleccion sin sacar el foco
 * del input, y Escape para cerrar. Es lo mismo que usa la paleta de comandos del
 * shell, por coherencia de teclado en toda la aplicacion.
 *
 * La busqueda es del lado del servidor (`ilike` sobre el `rec_name` del modelo
 * destino). Si la metadata del destino no se puede leer — puede pasar: los
 * permisos son por modelo — se degrada a traer la primera pagina y filtrar en el
 * cliente, que es peor pero sigue funcionando.
 *
 * La lista de resultados se dibuja en un PORTAL sobre `document.body`, con
 * posicion `fixed` calculada a partir del input. No es una preferencia estetica:
 * dentro de una grilla embebida el input vive en un `<td>` cuyo contenedor tiene
 * `overflow-x: auto` para poder desplazar la tabla a lo ancho, y el CSS dice que
 * un `overflow-x` distinto de `visible` fuerza el `overflow-y` a `auto` tambien.
 * Con la lista posicionada en `absolute` dentro de ese contenedor, las opciones
 * quedaban RECORTADAS: se renderizaban unos pixeles por debajo del borde de la
 * caja de desplazamiento y `document.elementFromPoint()` sobre la opcion
 * devolvia el boton «Agregar linea» que estaba detras. Consecuencia medida: el
 * click del mouse no elegia nada, el many2one obligatorio quedaba vacio y la
 * fila se enviaba igual — «invoice: Este campo no puede ser nulo». Sacando la
 * lista del arbol de contenedores, ningun `overflow` la puede tapar.
 *
 * Crear al vuelo («quick_create»)
 * -------------------------------
 * Cuando la busqueda vuelve SIN COINCIDENCIAS, el selector puede ofrecer crear
 * el registro ahi mismo con un mini-formulario. Es lo que convierte un alta de
 * tres pantallas en una: el caso testigo es el vendedor que consigue una
 * administracion de consorcios nueva y tiene que cargarla con «Ficha de
 * contacto» obligatoria, un campo que hasta hoy solo sabia BUSCAR — escribir un
 * nombre nuevo devolvia «Sin coincidencias» y el camino se terminaba ahi.
 *
 * Es GENERICO y se maneja por declaracion: el backend lo pide en
 * `FieldSpec.options.quick_create` y este componente no sabe nada del negocio.
 * Sirve para cualquier many2one de la aplicacion, no solo para ese.
 *
 * Cuatro guardas, y las cuatro importan:
 *
 * - **Solo si la busqueda dio CERO.** Con coincidencias parciales en pantalla,
 *   ofrecer «crear» es una fabrica de duplicados: el que ya existe esta ahi.
 * - **Solo si el usuario puede crear en el modelo destino.** El permiso llega
 *   ya evaluado en la metadata; dibujar un boton condenado a un 403 es peor que
 *   no dibujarlo.
 * - **Solo si todos los campos declarados son de texto.** Este componente no
 *   puede usar el registro de widgets (lo importaria en circulo: el registro
 *   registra al many2one, que usa este picker), asi que dibuja `<input
 *   type="text">` y nada mas. Si el modulo declara un campo que no sabe
 *   dibujar, NO ofrece el alta en vez de mandar un formulario incompleto que va
 *   a fallar del lado del servidor.
 * - **El error del servidor se muestra tal cual.** Si el CUIT ya existe, la
 *   restriccion de unicidad contesta en castellano y esa frase se lee adentro
 *   del mini-formulario, sin cerrar nada y sin perder lo tipeado.
 */

import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import type { MouseEvent } from 'react';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { controlClass } from '@/components/fields/controls';
import { normalize } from '@/components/shell/menu-utils';
import { useMetaQuery } from '@/components/shell/queries';
import { IconSpinner } from '@/components/shell/icons';
import { api, ApiError } from '@/lib/api';
import type { FieldMetaMap, JsonValue, Many2OneValue, RecordData } from '@/lib/types';
import { useSessionStore } from '@/store/session';

/** Registros que se ofrecen por busqueda. Mas que esto no se lee en una lista. */
const LIMITE = 20;
/** Cuando hay que filtrar en el cliente conviene traer un poco mas de material. */
const LIMITE_DEGRADADO = 100;
const ESPERA_TIPEO = 220;
/** Alto maximo de la lista, en px. Tiene que coincidir con el `max-h` del `<ul>`. */
const ALTO_LISTA = 256;
/** Alto maximo del mini-formulario de alta: mas alto que la lista, se desplaza. */
const ALTO_ALTA = 420;
/** Separacion entre el input y la lista, en px. */
const SEPARACION = 4;

/**
 * Tipos de campo que el mini-formulario sabe dibujar.
 *
 * Corta a proposito: cada tipo de mas seria un widget reimplementado acá, y el
 * lugar donde se dibujan los campos es el renderer generico, no un buscador.
 */
const TIPOS_QUE_SABE_DIBUJAR = new Set(['char', 'text', 'email', 'url']);

/**
 * Lo que el backend declara en `FieldSpec.options.quick_create`.
 *
 * @property fields  Campos del modelo DESTINO que se piden. Lo minimo para que
 *   el registro nazca completo: si falta uno que el modelo exige, el alta va a
 *   fallar del lado del servidor y el usuario va a leer por que.
 * @property defaults Valores fijos que viajan con el alta y que el usuario no
 *   ve ni edita (`is_company`, `is_customer`).
 * @property label   Titulo del mini-formulario.
 * @property help    Aclaracion debajo del titulo.
 */
export interface QuickCreateConfig {
  fields: string[];
  defaults: Record<string, JsonValue>;
  label: string;
  help: string;
}

/**
 * Lee la configuracion de `options` con desconfianza.
 *
 * `options` es un saco de JSON libre que viaja del backend al frontend: se
 * valida acá y cualquier cosa que no tenga la forma esperada devuelve `null`,
 * que significa «este campo no ofrece crear al vuelo». Un `options` mal escrito
 * tiene que apagar una comodidad, nunca romper un formulario.
 */
export function leerQuickCreate(options: Record<string, JsonValue> | undefined): QuickCreateConfig | null {
  const crudo = options?.quick_create;
  if (crudo === null || crudo === undefined || typeof crudo !== 'object' || Array.isArray(crudo)) {
    return null;
  }
  const datos = crudo as Record<string, JsonValue>;
  const campos = datos.fields;
  if (!Array.isArray(campos) || campos.length === 0) return null;
  const nombres = campos.filter((c): c is string => typeof c === 'string');
  if (nombres.length !== campos.length) return null;

  const defaults = datos.defaults;
  const label = datos.label;
  const help = datos.help;
  return {
    fields: nombres,
    defaults:
      defaults !== null && typeof defaults === 'object' && !Array.isArray(defaults)
        ? (defaults as Record<string, JsonValue>)
        : {},
    label: typeof label === 'string' ? label : 'Crear',
    help: typeof help === 'string' ? help : '',
  };
}

/** `true` si el mini-formulario puede dibujar todos los campos declarados. */
function sabeDibujar(config: QuickCreateConfig, campos: FieldMetaMap | undefined): boolean {
  if (campos === undefined) return false;
  return config.fields.every((nombre) => {
    const meta = campos[nombre];
    return meta !== undefined && !meta.readonly && TIPOS_QUE_SABE_DIBUJAR.has(meta.type);
  });
}

export interface RelationPickerProps {
  id: string;
  /** Nombre logico del modelo destino. */
  relation: string;
  onPick: (registro: Many2OneValue) => void;
  /** Ids ya elegidos: no se vuelven a ofrecer. */
  excludeIds?: number[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
  describedBy?: string;
  /** Texto inicial del input (el many2one muestra ahi lo ya elegido). */
  initialQuery?: string;
  autoFocus?: boolean;
  /**
   * Crear el registro destino sin salir del selector, cuando la busqueda no
   * encuentra nada. Ausente: el selector solo busca, como siempre.
   */
  quickCreate?: QuickCreateConfig | null;
}

export function RelationPicker({
  id,
  relation,
  onPick,
  excludeIds = [],
  placeholder = 'Buscar…',
  disabled = false,
  required = false,
  invalid = false,
  describedBy,
  initialQuery = '',
  autoFocus = false,
  quickCreate = null,
}: RelationPickerProps) {
  const [texto, setTexto] = useState(initialQuery);
  const [consulta, setConsulta] = useState(initialQuery);
  const [abierto, setAbierto] = useState(false);
  const [indice, setIndice] = useState(0);
  /** Mini-formulario de alta abierto: la lista se reemplaza por el. */
  const [creando, setCreando] = useState(false);
  const [borrador, setBorrador] = useState<Record<string, string>>({});
  const [guardando, setGuardando] = useState(false);
  const [errorAlta, setErrorAlta] = useState<string | null>(null);
  const contenedor = useRef<HTMLDivElement>(null);
  const campo = useRef<HTMLInputElement>(null);
  const listaId = useId();
  const altaId = useId();

  /** Rectangulo del input en coordenadas de viewport, para posicionar la lista. */
  const [caja, setCaja] = useState<{ left: number; top: number; width: number; arriba: boolean } | null>(
    null,
  );

  const medir = useCallback((): void => {
    const nodo = campo.current;
    if (nodo === null) return;
    const r = nodo.getBoundingClientRect();
    // Si abajo no entra la lista y arriba sí, se despliega hacia arriba. Sin
    // esto, un many2one en la ultima fila de una grilla larga abriria contra el
    // borde inferior de la ventana.
    const espacioAbajo = window.innerHeight - r.bottom;
    const arriba = espacioAbajo < ALTO_LISTA && r.top > espacioAbajo;
    setCaja({
      left: r.left,
      top: arriba ? r.top - SEPARACION : r.bottom + SEPARACION,
      width: r.width,
      arriba,
    });
  }, []);

  // Se mide al abrir y se vuelve a medir mientras este abierta: la pagina se
  // puede desplazar con el teclado o cambiar de tamaño con la lista desplegada,
  // y `position: fixed` no sigue al input solo. El `capture` es lo que hace que
  // tambien se entere del scroll de un contenedor interno —la grilla embebida—
  // y no solo del de la ventana.
  useLayoutEffect(() => {
    if (!abierto) {
      setCaja(null);
      return;
    }
    medir();
    window.addEventListener('scroll', medir, true);
    window.addEventListener('resize', medir);
    return () => {
      window.removeEventListener('scroll', medir, true);
      window.removeEventListener('resize', medir);
    };
  }, [abierto, medir]);

  const companyId = useSessionStore((estado) => estado.activeCompanyId);
  // La metadata del modelo destino recién cuando se abre el buscador: sólo
  // hace falta para buscar (`rec_name`) y para ofrecer crear al vuelo, y las
  // dos cosas pasan con el desplegable abierto. Pedirla al montar hacía que un
  // formulario con cinco relaciones disparara cinco pedidos de metadata —medio
  // segundo fijo cada uno— antes de que la persona tocara nada.
  const meta = useMetaQuery(abierto ? relation : null);
  const recName = meta.data?.rec_name ?? null;
  const metaResuelta = !meta.isPending;

  // Debounce: sin esto cada tecla dispara una consulta al backend.
  useEffect(() => {
    const temporizador = window.setTimeout(() => {
      setConsulta(texto);
    }, ESPERA_TIPEO);
    return () => {
      window.clearTimeout(temporizador);
    };
  }, [texto]);

  const busqueda = useQuery({
    queryKey: ['relation-picker', companyId, relation, recName, recName === null ? '' : consulta],
    queryFn: ({ signal }) =>
      recName === null
        ? api.searchRead(relation, { limit: LIMITE_DEGRADADO }, signal)
        : api.nameSearch(relation, recName, consulta, LIMITE, signal),
    enabled: abierto && metaResuelta,
    staleTime: 30_000,
  });

  const excluidos = new Set(excludeIds);
  const filtroCliente = normalize(consulta);
  const opciones = (busqueda.data?.results ?? [])
    .filter((registro) => !excluidos.has(registro.id))
    .filter(
      (registro) =>
        recName !== null || filtroCliente === '' || normalize(registro.display_name).includes(filtroCliente),
    )
    .slice(0, LIMITE);

  useEffect(() => {
    setIndice(0);
  }, [consulta, abierto]);

  const elegir = (posicion: number): void => {
    const opcion = opciones[posicion];
    if (opcion === undefined) return;
    onPick({ id: opcion.id, display_name: opcion.display_name });
    setTexto('');
    setConsulta('');
    setAbierto(false);
  };

  // -- Crear al vuelo ------------------------------------------------------
  const camposDestino = meta.data?.fields;
  const puedeCrear =
    quickCreate !== null &&
    !disabled &&
    meta.data?.permissions.create === true &&
    sabeDibujar(quickCreate, camposDestino);
  // Solo con la busqueda RESUELTA y vacia: mientras esta buscando, «no hay» es
  // una respuesta que todavia no se dio. Y con el texto vacio tampoco: no hay
  // nada que crear.
  const ofreceCrear =
    puedeCrear &&
    texto.trim() !== '' &&
    consulta.trim() === texto.trim() &&
    opciones.length === 0 &&
    !busqueda.isPending &&
    !busqueda.isFetching &&
    !busqueda.isError;

  const abrirAlta = (): void => {
    if (quickCreate === null) return;
    // Lo tipeado precarga el campo con el que se muestra el registro: es lo que
    // la persona ya escribio, y volver a pedirselo seria cobrarle el tipeo dos
    // veces.
    const principal = quickCreate.fields.includes(recName ?? '') ? (recName as string) : quickCreate.fields[0];
    const inicial: Record<string, string> = {};
    for (const nombre of quickCreate.fields) {
      inicial[nombre] = nombre === principal ? texto.trim() : '';
    }
    setBorrador(inicial);
    setErrorAlta(null);
    setCreando(true);
  };

  const cancelarAlta = (): void => {
    setCreando(false);
    setErrorAlta(null);
    setGuardando(false);
    campo.current?.focus();
  };

  const confirmarAlta = (): void => {
    if (quickCreate === null || guardando) return;
    setGuardando(true);
    setErrorAlta(null);
    const valores: Record<string, JsonValue> = { ...quickCreate.defaults };
    for (const [nombre, valor] of Object.entries(borrador)) {
      valores[nombre] = valor.trim();
    }
    api
      .createRecord<RecordData>(relation, valores)
      .then((registro) => {
        // `display_name` lo arma el backend con el `rec_name` del modelo, que no
        // siempre es el campo que se tipeo: se usa el que vuelve, y lo tipeado
        // solo si viniera vacio.
        const nombre = registro.display_name !== '' ? registro.display_name : texto.trim();
        onPick({ id: registro.id, display_name: nombre });
        setGuardando(false);
        setCreando(false);
        setTexto('');
        setConsulta('');
        setAbierto(false);
      })
      .catch((error: unknown) => {
        // El mensaje del backend TAL CUAL: ya viene en castellano y nombrando el
        // dato (el caso testigo es el CUIT repetido, que dice de quien es). Un
        // «no se pudo crear» generico borraria justamente lo que hace falta
        // saber para arreglarlo.
        setErrorAlta(
          error instanceof ApiError ? error.message : 'No se pudo crear. Probá de nuevo.',
        );
        setGuardando(false);
      });
  };

  return (
    <div
      ref={contenedor}
      className="relative"
      onBlur={(evento) => {
        // Solo se cierra si el foco salio del componente entero: si no, elegir
        // con el mouse cerraria la lista antes del click.
        //
        // Con el mini-formulario de alta abierto NO se cierra nunca por foco: el
        // formulario vive en el portal, que cuelga de `body` y por lo tanto esta
        // FUERA de este contenedor, asi que tabular hasta su primer campo cuenta
        // como «el foco salio» y desmontaria el formulario en el que la persona
        // acaba de entrar. Se cierra con «Cancelar» o con Escape, que es lo que
        // corresponde a un formulario con datos a medio escribir.
        if (!creando && !evento.currentTarget.contains(evento.relatedTarget as Node | null)) {
          setAbierto(false);
        }
      }}
    >
      <input
        ref={campo}
        id={id}
        type="text"
        role="combobox"
        autoComplete="off"
        autoFocus={autoFocus}
        aria-expanded={abierto}
        aria-controls={listaId}
        aria-autocomplete="list"
        aria-activedescendant={abierto && opciones.length > 0 ? `${listaId}-${indice}` : undefined}
        aria-invalid={invalid || undefined}
        aria-required={required || undefined}
        aria-describedby={describedBy}
        disabled={disabled}
        placeholder={placeholder}
        className={controlClass(invalid, 'pr-9')}
        value={texto}
        onChange={(evento) => {
          setTexto(evento.target.value);
          setAbierto(true);
        }}
        onFocus={() => {
          setAbierto(true);
        }}
        onKeyDown={(evento) => {
          if (evento.key === 'ArrowDown') {
            evento.preventDefault();
            setAbierto(true);
            setIndice((actual) => (opciones.length === 0 ? 0 : (actual + 1) % opciones.length));
          } else if (evento.key === 'ArrowUp') {
            evento.preventDefault();
            setIndice((actual) => (opciones.length === 0 ? 0 : (actual - 1 + opciones.length) % opciones.length));
          } else if (evento.key === 'Enter') {
            if (abierto && opciones.length > 0) {
              evento.preventDefault();
              elegir(indice);
            } else if (ofreceCrear) {
              // Sin coincidencias, Enter abre el alta. Es el gesto que la
              // persona ya hizo: escribio un nombre que no existe y apreto
              // Enter esperando que pase algo.
              evento.preventDefault();
              abrirAlta();
            }
          } else if (evento.key === 'Escape') {
            if (abierto) {
              evento.stopPropagation();
              setAbierto(false);
            }
          }
        }}
      />

      {busqueda.isFetching && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-fg-faint">
          <IconSpinner className="h-4 w-4" />
        </span>
      )}

      {abierto &&
        caja !== null &&
        createPortal(
          <div
            // El portal cuelga de `body`, asi que el `onBlur` del contenedor no
            // lo alcanza: `mousedown` con `preventDefault()` en la opcion es lo
            // que impide que el input pierda el foco y la lista se cierre antes
            // de tiempo. Es el mismo mecanismo que ya usaba en `absolute`.
            className={clsx(
              'fixed z-50 rounded-lg border border-line bg-surface shadow-lg animate-fade-in',
              // El mini-formulario es mas alto que la lista y tiene que poder
              // desplazarse: con `overflow-hidden` y el alto de la lista, los
              // botones «Cancelar» y «Crear» quedaban recortados abajo — un
              // formulario sin forma de confirmarlo.
              creando ? 'overflow-y-auto' : 'overflow-hidden',
            )}
            style={{
              left: caja.left,
              width: caja.width,
              ...(caja.arriba
                ? { bottom: window.innerHeight - caja.top, maxHeight: creando ? ALTO_ALTA : ALTO_LISTA }
                : { top: caja.top, maxHeight: creando ? ALTO_ALTA : ALTO_LISTA }),
            }}
            {...(creando
              ? {}
              : {
                  onMouseDown: (evento: MouseEvent) => {
                    // Cualquier click dentro de la lista (la barra de
                    // desplazamiento, por ejemplo) tampoco puede sacarle el foco
                    // al input. Con el alta abierta NO se aplica: los campos del
                    // mini-formulario necesitan poder recibir el foco, y esto se
                    // lo impediria.
                    evento.preventDefault();
                  },
                })}
          >
          {creando && quickCreate !== null ? (
            <form
              id={altaId}
              className="flex flex-col gap-3 p-3"
              onSubmit={(evento) => {
                evento.preventDefault();
                confirmarAlta();
              }}
              onKeyDown={(evento) => {
                if (evento.key === 'Escape') {
                  evento.stopPropagation();
                  cancelarAlta();
                }
              }}
            >
              <div>
                <p className="text-sm font-medium text-fg">{quickCreate.label}</p>
                {quickCreate.help !== '' && (
                  <p className="mt-0.5 text-xs text-fg-muted">{quickCreate.help}</p>
                )}
              </div>

              {quickCreate.fields.map((nombre, posicion) => {
                const metaCampo = camposDestino?.[nombre];
                return (
                  <div key={nombre}>
                    <label
                      htmlFor={`${altaId}-${nombre}`}
                      className="mb-1 block text-xs font-medium text-fg-muted"
                    >
                      {metaCampo?.label ?? nombre}
                      {metaCampo?.required === true && <span className="text-danger"> *</span>}
                    </label>
                    <input
                      id={`${altaId}-${nombre}`}
                      type="text"
                      autoComplete="off"
                      autoFocus={posicion === 0}
                      disabled={guardando}
                      className={controlClass(false)}
                      value={borrador[nombre] ?? ''}
                      onChange={(evento) => {
                        const valor = evento.target.value;
                        setBorrador((actual) => ({ ...actual, [nombre]: valor }));
                      }}
                    />
                    {metaCampo?.help !== undefined && metaCampo.help !== '' && (
                      <p className="mt-1 text-xs text-fg-faint">{metaCampo.help}</p>
                    )}
                  </div>
                );
              })}

              {errorAlta !== null && (
                <p role="alert" className="text-xs text-danger">
                  {errorAlta}
                </p>
              )}

              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={cancelarAlta}
                  className="rounded-md border border-line px-3 py-1.5 text-sm text-fg-muted transition-colors hover:border-line-strong hover:text-fg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={guardando}
                  className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-accent-contrast transition-opacity disabled:opacity-60"
                >
                  {guardando && <IconSpinner className="h-3.5 w-3.5" />}
                  Crear
                </button>
              </div>
            </form>
          ) : (
          <ul id={listaId} role="listbox" aria-label={`Resultados de ${relation}`} className="max-h-64 overflow-y-auto py-1">
            {opciones.map((opcion, posicion) => (
              <li
                key={opcion.id}
                id={`${listaId}-${posicion}`}
                role="option"
                aria-selected={posicion === indice}
                className={clsx(
                  'cursor-pointer px-3 py-2 text-sm',
                  posicion === indice ? 'bg-accent-soft text-accent' : 'text-fg',
                )}
                onMouseEnter={() => {
                  setIndice(posicion);
                }}
                onMouseDown={(evento) => {
                  // `mousedown` y no `click`: el click llega despues del blur.
                  evento.preventDefault();
                  elegir(posicion);
                }}
              >
                {opcion.display_name}
              </li>
            ))}

            {opciones.length === 0 && (
              <li className="px-3 py-3 text-sm text-fg-muted" role="presentation">
                {busqueda.isPending || busqueda.isFetching
                  ? 'Buscando…'
                  : busqueda.isError
                    ? 'No se pudo buscar. Revisá tus permisos sobre este dato.'
                    : 'Sin coincidencias.'}
              </li>
            )}

            {/* La salida que antes no existia: sin coincidencias, el camino se
                terminaba aca. Va al final y solo cuando no hay nada que elegir,
                para no competir con un registro que ya existe. */}
            {ofreceCrear && quickCreate !== null && (
              <li className="border-t border-line px-1 pt-1" role="presentation">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm text-accent transition-colors hover:bg-accent-soft"
                  onMouseDown={(evento) => {
                    // `mousedown` y no `click`, por lo mismo que las opciones: el
                    // click llegaria despues del blur y el portal ya no estaria.
                    evento.preventDefault();
                    abrirAlta();
                  }}
                >
                  <span aria-hidden="true" className="text-base leading-none">
                    +
                  </span>
                  <span className="truncate">Crear «{texto.trim()}»</span>
                </button>
              </li>
            )}
          </ul>
          )}
          </div>,
          document.body,
        )}
    </div>
  );
}
